import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import i18n from "../../config/i18n";
import { sharedApi, getAuthToken } from "../../services/sharedApi";

import type {
  PaymentsState,
  PaymentError,
  BackendSubscriptionPlan,
  UserSubscription,
} from "../../features/payments/types";
import type { PayloadAction } from "@reduxjs/toolkit";

const initialState: PaymentsState = {
  plans: [],
  userSubscription: null,
  checkout: {
    sessionId: null,
    url: null,
    loading: false,
    error: null,
  },
  ui: {
    loading: false,
    error: null,
    showCancelDialog: false,
    showUpgradeDialog: false,
    selectedPlanId: null,
  },
};

// Async thunks
export const fetchPlans = createAsyncThunk<
  BackendSubscriptionPlan[],
  void,
  { rejectValue: PaymentError }
>("payments/fetchPlans", async (_, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    const plansResp = await sharedApi.subscriptions.getPlans(token);
    return plansResp.map(
      (plan): BackendSubscriptionPlan => ({
        id: plan.id,
        name: plan.name,
        description: plan.description ?? "",
        price: plan.monthlyPrice,
        currency: plan.currency,
        interval: "MONTHLY",
        textsPerMonth: plan.textsPerMonth,
        isActive: plan.isActive,
        features: plan.features.map((feature) => ({
          ...feature,
          featureName: feature.featureKey,
        })),
      }),
    );
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue({ message: error.message } as PaymentError);
    }
    return rejectWithValue({
      message: i18n.t("common:interface.unknownErrorOccurred"),
    } as PaymentError);
  }
});

export const fetchUserSubscriptionData = createAsyncThunk<
  UserSubscription | null,
  void,
  { rejectValue: PaymentError }
>("payments/fetchUserSubscription", async (_, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    const subResp = await sharedApi.subscriptions.getMySubscription(token);
    const subscription = subResp.active;
    return subscription
      ? {
          ...subscription,
          status: subscription.status.toLowerCase(),
          endDate: subscription.endDate ?? undefined,
          plan: {
            ...subscription.plan,
            description: subscription.plan.description ?? undefined,
          },
        }
      : null;
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue({ message: error.message } as PaymentError);
    }
    return rejectWithValue({
      message: i18n.t("common:interface.unknownErrorOccurred"),
    } as PaymentError);
  }
});

export const createCheckout = createAsyncThunk<
  { sessionId: string; url: string },
  { planId: string; billingPeriod: "monthly" | "yearly" },
  { rejectValue: PaymentError }
>(
  "payments/createCheckout",
  async ({ planId, billingPeriod }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const checkout = await sharedApi.stripe.createCheckoutSession(
        { planId, billingPeriod },
        token,
      );
      interface CheckoutResponse {
        sessionId?: string;
        url?: string;
      }
      const typedCheckout = checkout as CheckoutResponse;
      return {
        sessionId: typedCheckout.sessionId || "",
        url: typedCheckout.url || "",
      };
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue({ message: error.message } as PaymentError);
      }
      return rejectWithValue({
        message: i18n.t("common:interface.unknownErrorOccurred"),
      } as PaymentError);
    }
  },
);

export const cancelUserSubscription = createAsyncThunk<
  null,
  string,
  { rejectValue: PaymentError }
>(
  "payments/cancelSubscription",
  async (subscriptionId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      await sharedApi.subscriptions.cancel(subscriptionId, undefined, token);
      return null; // Subscription cancelled
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue({ message: error.message } as PaymentError);
      }
      return rejectWithValue({
        message: i18n.t("common:interface.unknownErrorOccurred"),
      } as PaymentError);
    }
  },
);

const paymentsSlice = createSlice({
  name: "payments",
  initialState,
  reducers: {
    clearError: (state) => {
      state.ui.error = null;
      state.checkout.error = null;
    },
    showCancelDialog: (state) => {
      state.ui.showCancelDialog = true;
    },
    hideCancelDialog: (state) => {
      state.ui.showCancelDialog = false;
    },
    showUpgradeDialog: (state, action: PayloadAction<string>) => {
      state.ui.showUpgradeDialog = true;
      state.ui.selectedPlanId = action.payload;
    },
    hideUpgradeDialog: (state) => {
      state.ui.showUpgradeDialog = false;
      state.ui.selectedPlanId = null;
    },
    clearCheckoutSession: (state) => {
      state.checkout.sessionId = null;
      state.checkout.url = null;
      state.checkout.error = null;
    },
    resetPaymentsState: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch plans
    builder
      .addCase(fetchPlans.pending, (state) => {
        state.ui.loading = true;
        state.ui.error = null;
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.ui.loading = false;
        state.plans = action.payload;
      })
      .addCase(fetchPlans.rejected, (state, action) => {
        state.ui.loading = false;
        state.ui.error = action.payload as PaymentError;
      });

    // Fetch user subscription
    builder
      .addCase(fetchUserSubscriptionData.pending, (state) => {
        state.ui.loading = true;
        state.ui.error = null;
      })
      .addCase(fetchUserSubscriptionData.fulfilled, (state, action) => {
        state.ui.loading = false;
        state.userSubscription = action.payload;
      })
      .addCase(fetchUserSubscriptionData.rejected, (state, action) => {
        state.ui.loading = false;
        state.ui.error = action.payload as PaymentError;
      });

    // Create checkout
    builder
      .addCase(createCheckout.pending, (state) => {
        state.checkout.loading = true;
        state.checkout.error = null;
      })
      .addCase(createCheckout.fulfilled, (state, action) => {
        state.checkout.loading = false;
        state.checkout.sessionId = action.payload.sessionId;
        state.checkout.url = action.payload.url;
      })
      .addCase(createCheckout.rejected, (state, action) => {
        state.checkout.loading = false;
        state.checkout.error = action.payload as PaymentError;
      });

    // Cancel subscription
    builder
      .addCase(cancelUserSubscription.pending, (state) => {
        state.ui.loading = true;
        state.ui.error = null;
      })
      .addCase(cancelUserSubscription.fulfilled, (state) => {
        state.ui.loading = false;
        state.userSubscription = null;
        state.ui.showCancelDialog = false;
      })
      .addCase(cancelUserSubscription.rejected, (state, action) => {
        state.ui.loading = false;
        state.ui.error = action.payload as PaymentError;
      });
  },
});

export const {
  clearError,
  showCancelDialog,
  hideCancelDialog,
  showUpgradeDialog,
  hideUpgradeDialog,
  clearCheckoutSession,
  resetPaymentsState,
} = paymentsSlice.actions;

export default paymentsSlice.reducer;
